import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Package, Layers, ChevronRight, Loader2 } from "lucide-react";
import ProductGridCard from "../components/ProductGridCard";
import FeaturedProductDisplay from "../components/FeaturedProductDisplay";
import Navbar from "../components/nav/Navbar";
import HeroSection from "../components/HeroSection";
import { WebAdBanner, MobileAdToast } from "../components/AdBanner";
import SiteFooter from "../components/SiteFooter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";


// const URL = process.env.BACKEND_URL;
const URL = process.env.REACT_APP_BACKEND_URL;
const API = `${URL}/api`;

// Human-friendly labels and descriptions for each machine_type
const CATEGORY_META = {
  "Combo Snack+Drink": {
    label: "Combo Machines",
    description: "Snacks and cold drinks together — maximise revenue in one cabinet.",
  },
  "Standard Snack": {
    label: "Snack Machines",
    description: "High-capacity ambient snack dispensing for any location.",
  },
  "Refrigerated": {
    label: "Refrigerated Machines",
    description: "Chilled drinks, fresh food, and dairy — AI-vision or coil.",
  },
  "Frozen": {
    label: "Frozen & Ice Cream",
    description: "Frozen meals, novelties, and ice cream, round the clock.",
  },
  "Custom": {
    label: "Custom & Micro-Market",
    description: "White-label hardware with SDK access for custom deployments.",
  },
  "Arcade Game": {
    label: "Arcade Games",
    description: "Classic and modern arcade cabinets for entertainment venues.",
  },
  "Pinball Machine": {
    label: "Pinball Machines",
    description: "Full-size pinball units for arcades, bars, and rec rooms.",
  },
  "Prize Redemption": {
    label: "Prize Redemption",
    description: "Ticket-to-prize and instant-win redemption games.",
  },
  "Crane/Claw Machine": {
    label: "Crane & Claw",
    description: "Claw machines and skill-based prize games.",
  },
  "Token Dispenser": {
    label: "Token Dispensers",
    description: "Bill-to-token and card-to-token exchange units.",
  },
  "Ticket Dispenser": {
    label: "Ticket Dispensers",
    description: "Automated ticket issuance for events and transit.",
  },
};

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div
      className="animate-pulse flex flex-col overflow-hidden border border-border bg-card"
      style={{ borderRadius: "var(--radius)", width: 270, height: 340, flexShrink: 0 }}
    >
      <div className="bg-muted" style={{ height: 160 }} />
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="h-2.5 bg-muted rounded w-1/2" />
        <div className="h-3.5 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2 mt-auto" />
        <div className="flex gap-1.5 mt-1">
          <div className="h-6 bg-muted rounded flex-1" />
          <div className="h-6 bg-muted rounded w-12" />
        </div>
      </div>
    </div>
  );
}

// ── Category section ──────────────────────────────────────────────────────────
function CategoryRow({ category, products, loading, isThird = false }) {
  const navigate = useNavigate();
  const meta = CATEGORY_META[category] || { label: category, description: "" };

  const MAX_VISIBLE = isThird ? 3 : 8;
  const displayProducts = products.slice(0, MAX_VISIBLE);
  const hasMore = products.length > MAX_VISIBLE;

  return (
    <section>
      {/* Section header */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-heading font-black text-xl text-foreground leading-tight">
            {meta.label}
          </h2>
          {meta.description && (
            <p className="font-body text-xs text-muted-foreground mt-0.5">
              {meta.description}
            </p>
          )}
        </div>
        <button
          onClick={() =>
            navigate(`/shop?category=${encodeURIComponent(category)}`)
          }
          className="hidden sm:flex items-center gap-1 font-body text-xs text-muted-foreground hover:text-foreground transition-colors">
          View all
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Card grid + optional inline ad for the 3rd row */}
      <div className="flex gap-6 items-start">
        {/* Cards column */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-4">
            {loading ? (
              Array.from({ length: isThird ? 3 : 4 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))
            ) : displayProducts.length > 0 ? (
              displayProducts.map((p) => (
                <ProductGridCard key={p.mongo_id || p.id} product={p} />
              ))
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground/50 py-8 pl-2">
                <Package className="h-5 w-5" />
                <span className="font-body text-sm italic">
                  No products in this category yet.
                </span>
              </div>
            )}
          </div>

          {hasMore && !loading && (
            <button
              onClick={() =>
                navigate(`/shop?category=${encodeURIComponent(category)}`)
              }
              className="mt-4 flex items-center gap-1.5 font-body text-sm font-semibold transition-colors"
              style={{
                color: "var(--vb-blue)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}>
              View all {products.length} machines
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Inline ad — only on the 3rd row, desktop only */}
        {isThird && (
          <div
            className="hidden lg:block flex-shrink-0"
            style={{ width: 300, paddingTop: 4 }}>
            <WebAdBanner slot={0} />
          </div>
        )}
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ProductGridPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterCategory = searchParams.get("category") || "";
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [featured, setFeatured] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [loadedCategories, setLoadedCategories] = useState(new Set());
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ customer_name: "", email: "", phone: "", company_name: "", location: "", quantity: 1, customization_details: "", budget_range: "" });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);

  // ── Category filter ──
  useEffect(() => {
    if (!filterCategory) { setFilteredProducts(null); return; }
    setFilterLoading(true);
    axios.get(`${API}/products`, { params: { machine_type: filterCategory, status: "Active" } })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        if (data.length === 0) {
          navigate("/shop", { replace: true });
        } else {
          setFilteredProducts(data);
        }
      })
      .catch(() => navigate("/shop", { replace: true }))
      .finally(() => setFilterLoading(false));
  }, [filterCategory, navigate]);

  // Fetch categories
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products`, {
          params: { status: "Active" },
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        });
        const allProducts = Array.isArray(res.data) ? res.data : [];
        const catMap = {};
        allProducts.forEach((p) => {
          if (p.machine_type && !catMap[p.machine_type]) {
            catMap[p.machine_type] = { machine_type: p.machine_type };
          }
        });
        setCategories(Object.values(catMap));
      } catch (err) {
        console.error("Failed to load categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    };
    load();
  }, []);

  // Fetch featured products
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products`, {
          params: { status: "Active" },
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        });
        const allProducts = Array.isArray(res.data) ? res.data : [];
        const featured = [];
        if (allProducts.length > 0) featured.push(allProducts[0]);
        if (allProducts.length > 1) featured.push(allProducts[allProducts.length - 1]);
        setFeatured(featured);
      } catch (err) {
        console.error("Failed to load featured products:", err);
      } finally {
        setLoadingFeatured(false);
      }
    };
    load();
  }, []);

  // Fetch products per category
  const fetchCategory = useCallback(async (machineType) => {
    if (loadedCategories.has(machineType)) return;
    setLoadedCategories((prev) => new Set([...prev, machineType]));
    setProductsByCategory((prev) => ({ ...prev, [machineType]: null }));
    try {
      const res = await axios.get(`${API}/products`, {
        params: { machine_type: machineType, status: "Active" },
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });
      let data = Array.isArray(res.data) ? res.data : [];
      if (data.length === 0) {
        try {
          const fallback = await axios.get(`${API}/products`, {
            params: { status: "Active" },
            headers: { "Content-Type": "application/json", Accept: "application/json" },
          });
          data = (Array.isArray(fallback.data) ? fallback.data : []).filter(
            (p) => p.machine_type === machineType
          );
        } catch { /* fallback also failed */ }
      }
      setProductsByCategory((prev) => ({ ...prev, [machineType]: data }));
    } catch (err) {
      console.error(`Failed to load products for ${machineType}:`, err);
      setProductsByCategory((prev) => ({ ...prev, [machineType]: [] }));
    }
  }, [loadedCategories]);

  useEffect(() => {
    categories.forEach((cat) => fetchCategory(cat.machine_type));
  }, [categories, fetchCategory]);

  const DISPLAY_ORDER = [
    "Combo Snack+Drink",
    "Standard Snack",
    "Refrigerated",
    "Frozen",
    "Custom",
    "Arcade Game",
    "Pinball Machine",
    "Prize Redemption",
    "Crane/Claw Machine",
    "Token Dispenser",
    "Ticket Dispenser",
  ];

  const sortedCategories = [
    ...DISPLAY_ORDER.filter((t) => categories.some((c) => c.machine_type === t)),
    ...categories
      .filter((c) => !DISPLAY_ORDER.includes(c.machine_type))
      .map((c) => c.machine_type),
  ];

  const midpoint = Math.ceil(sortedCategories.length / 2);
  const firstHalf = sortedCategories.slice(0, midpoint);
  const secondHalf = sortedCategories.slice(midpoint);

  const catMeta = CATEGORY_META[filterCategory] || { label: filterCategory, description: "" };

  /* ── Filtered category view ── */
  if (filterCategory) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar onCtaClick={() => setQuoteOpen(true)} ctaLabel="Get Business Pricing" />
        <div style={{ paddingTop: "var(--header-h)" }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
            <button
              onClick={() => navigate("/shop")}
              className="flex items-center gap-1.5 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              ← Back to all machines
            </button>
            <div className="mb-8">
              <h1 className="font-heading font-black text-3xl text-foreground leading-tight">
                {catMeta.label}
              </h1>
              {catMeta.description && (
                <p className="font-body text-sm text-muted-foreground mt-1">
                  {catMeta.description}
                </p>
              )}
            </div>
            {filterLoading ? (
              <div className="flex flex-wrap gap-4">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : filteredProducts && filteredProducts.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {filteredProducts.map((p) => (
                  <ProductGridCard key={p.mongo_id || p.id} product={p} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Package className="h-10 w-10 text-muted-foreground/30" />
                <p className="font-body text-sm text-muted-foreground">
                  No machines found in this category.
                </p>
                <button
                  onClick={() => navigate("/shop")}
                  className="btn-primary"
                  style={{ padding: "10px 24px", fontSize: 13 }}
                >
                  Browse All Machines
                </button>
              </div>
            )}
            {filteredProducts && filteredProducts.length > 0 && (
              <p className="font-mono text-xs text-muted-foreground mt-6">
                {filteredProducts.length} machine{filteredProducts.length !== 1 ? "s" : ""} in {catMeta.label}
              </p>
            )}
          </div>
        </div>

        <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-semibold text-2xl text-foreground text-center">
                Get Business Pricing
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setQuoteSubmitting(true);
                try {
                  await axios.post(`${API}/quotes`, { ...quoteForm });
                  toast.success("Quote submitted! We'll be in touch within 24 hours.");
                  setQuoteOpen(false);
                  setQuoteForm({ customer_name: "", email: "", phone: "", company_name: "", location: "", quantity: 1, customization_details: "", budget_range: "" });
                } catch { toast.error("Failed to submit. Please try again."); }
                finally { setQuoteSubmitting(false); }
              }}
              className="space-y-6 mt-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Full Name *</Label><Input name="customer_name" value={quoteForm.customer_name} onChange={e => setQuoteForm(p => ({ ...p, customer_name: e.target.value }))} required className="mt-1" /></div>
                <div><Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Email *</Label><Input type="email" value={quoteForm.email} onChange={e => setQuoteForm(p => ({ ...p, email: e.target.value }))} required className="mt-1" /></div>
                <div><Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Phone *</Label><Input type="tel" value={quoteForm.phone} onChange={e => setQuoteForm(p => ({ ...p, phone: e.target.value }))} required className="mt-1" /></div>
                <div><Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Company</Label><Input value={quoteForm.company_name} onChange={e => setQuoteForm(p => ({ ...p, company_name: e.target.value }))} className="mt-1" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Quantity</Label><Input type="number" min="1" value={quoteForm.quantity} onChange={e => setQuoteForm(p => ({ ...p, quantity: e.target.value }))} className="mt-1" /></div>
                <div><Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Location</Label><Input placeholder="City, State" value={quoteForm.location} onChange={e => setQuoteForm(p => ({ ...p, location: e.target.value }))} className="mt-1" /></div>
              </div>
              <button type="submit" disabled={quoteSubmitting} className="btn-primary w-full justify-center">
                {quoteSubmitting ? "Submitting..." : "Submit Quote Request"}
              </button>
            </form>
          </DialogContent>
        </Dialog>

        <SiteFooter />
      </div>
    );
  }

  /* ── Full grid view ── */
  return (
    <div className="min-h-screen bg-background">
      <Navbar
        transparent
        onCtaClick={() => setQuoteOpen(true)}
        ctaLabel="Get Business Pricing"
      />

      <HeroSection variant="shop" onQuoteClick={() => setQuoteOpen(true)} />

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />

      <MobileAdToast />

      <div id="market" className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        <div className="flex flex-col gap-16">
          {loadingCategories
            ? Array.from({ length: 2 }).map((_, i) => (
                <CategoryRow
                  key={`skel-${i}`}
                  category="Loading"
                  products={[]}
                  loading={true}
                  isThird={i === 2}
                />
              ))
            : firstHalf.map((machineType, i) => (
                <CategoryRow
                  key={machineType}
                  category={machineType}
                  products={productsByCategory[machineType] ?? []}
                  loading={productsByCategory[machineType] === null}
                  isThird={
                    i === 2 ||
                    (firstHalf.length < 3 && i === firstHalf.length - 1)
                  }
                />
              ))}

          {!loadingFeatured && featured[0] && (
            <section>
              <div className="flex items-center gap-2 mb-5">
                <Layers className="h-4 w-4 text-primary" />
                <h2 className="font-heading font-black text-xl text-foreground">
                  Featured Machine
                </h2>
              </div>
              <FeaturedProductDisplay
                product={featured[0]}
                reverse={false}
                accent="primary"
              />
            </section>
          )}

          {!loadingCategories &&
            secondHalf.map((machineType, i) => {
              // The ad already renders in firstHalf (on i===2 or last row if <3).
              // In secondHalf we never repeat it — isThird is always false here.
              return (
                <CategoryRow
                  key={machineType}
                  category={machineType}
                  products={productsByCategory[machineType] ?? []}
                  loading={productsByCategory[machineType] === null}
                  isThird={false}
                />
              );
            })}

          {!loadingFeatured && featured[1] && (
            <section>
              <div className="flex items-center gap-2 mb-5">
                <Layers className="h-4 w-4 text-primary" />
                <h2 className="font-heading font-black text-xl text-foreground">
                  Just Added
                </h2>
              </div>
              <FeaturedProductDisplay
                product={featured[1]}
                reverse={true}
                accent="dark"
              />
            </section>
          )}

          {loadingFeatured && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/40" />
            </div>
          )}

          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-6 px-8 py-8"
            style={{
              borderRadius: "var(--radius-card)",
              background: "var(--grad-subtle)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}>
            <div>
              <h3 className="font-heading font-black text-xl text-foreground mb-1">
                Need help choosing?
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                We'll match you with the right machine for your location and
                revenue goals.
              </p>
            </div>
            <button
              onClick={() => setQuoteOpen(true)}
              className="flex-shrink-0 font-body text-sm text-primary cursor-pointer transition-colors hover:text-primary/80"
              style={{
                textDecoration: "underline",
                textUnderlineOffset: 3,
                background: "none",
                border: "none",
              }}>
              Get a custom quote →
            </button>
          </div>
        </div>
      </div>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl text-foreground text-center">
              Get Business Pricing
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setQuoteSubmitting(true);
              try {
                await axios.post(`${API}/quotes`, { ...quoteForm });
                toast.success(
                  "Quote submitted! We'll be in touch within 24 hours.",
                );
                setQuoteOpen(false);
                setQuoteForm({
                  customer_name: "",
                  email: "",
                  phone: "",
                  company_name: "",
                  location: "",
                  quantity: 1,
                  customization_details: "",
                  budget_range: "",
                });
              } catch {
                toast.error("Failed to submit. Please try again.");
              } finally {
                setQuoteSubmitting(false);
              }
            }}
            className="space-y-6 mt-4">
            <div className="space-y-4">
              <h3 className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                Contact Information
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Full Name *
                  </Label>
                  <Input
                    name="customer_name"
                    value={quoteForm.customer_name}
                    onChange={(e) =>
                      setQuoteForm((p) => ({
                        ...p,
                        customer_name: e.target.value,
                      }))
                    }
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={quoteForm.email}
                    onChange={(e) =>
                      setQuoteForm((p) => ({ ...p, email: e.target.value }))
                    }
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Phone *
                  </Label>
                  <Input
                    type="tel"
                    value={quoteForm.phone}
                    onChange={(e) =>
                      setQuoteForm((p) => ({ ...p, phone: e.target.value }))
                    }
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Company
                  </Label>
                  <Input
                    value={quoteForm.company_name}
                    onChange={(e) =>
                      setQuoteForm((p) => ({
                        ...p,
                        company_name: e.target.value,
                      }))
                    }
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  Quantity *
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={quoteForm.quantity}
                  onChange={(e) =>
                    setQuoteForm((p) => ({ ...p, quantity: e.target.value }))
                  }
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  Delivery Location *
                </Label>
                <Input
                  placeholder="City, State, Country"
                  value={quoteForm.location}
                  onChange={(e) =>
                    setQuoteForm((p) => ({ ...p, location: e.target.value }))
                  }
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  Budget Range
                </Label>
                <Select
                  value={quoteForm.budget_range}
                  onValueChange={(v) =>
                    setQuoteForm((p) => ({ ...p, budget_range: v }))
                  }>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_3000">Under $3,000</SelectItem>
                    <SelectItem value="3000_5000">$3,000 – $5,000</SelectItem>
                    <SelectItem value="5000_10000">$5,000 – $10,000</SelectItem>
                    <SelectItem value="10000_plus">$10,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Notes / Customization
              </Label>
              <Textarea
                placeholder="Branding requirements, special needs..."
                value={quoteForm.customization_details}
                onChange={(e) =>
                  setQuoteForm((p) => ({
                    ...p,
                    customization_details: e.target.value,
                  }))
                }
                className="mt-1 min-h-[90px]"
              />
            </div>
            <button
              type="submit"
              disabled={quoteSubmitting}
              className="btn-primary w-full justify-center">
              {quoteSubmitting ? "Submitting..." : "Submit Quote Request"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <SiteFooter />
    </div>
  );
}
